/**
 * features/finance/FeeStatusOverview.tsx
 * ------------------------------------------------------------------
 * IS EVERY PUPIL ACCOUNTED FOR THIS TERM?
 *
 * A billing run charges the pupils it can see. The one it cannot see is
 * the mid-term joiner enrolled the week after it ran — and nothing in
 * the system asks about them again. They can drift unbilled for a whole
 * term, and the school finds out when the year's income does not add up.
 *
 * SO THE HEADLINE IS THE UNACCOUNTED COUNT, school-wide, before any
 * class is chosen. A screen that made you pick a class first would only
 * find them if somebody happened to look at the right one.
 *
 * "NO INVOICE" IS NOT ONE STATE. A pupil whose band is billed in other
 * terms has no invoice and is perfectly fine; a pupil nobody charged has
 * no invoice and is a problem. Collapsing the two is what lets the
 * second hide inside the first, so lib/feeStatus separates them and this
 * screen shows the reason on every row.
 *
 * NO STAFF-PAYROLL MODE. LKC has not settled its staff-fee policy, and
 * offering a mode nobody can be put into would make the register read as
 * though somebody had checked.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  ReceiptText, AlertTriangle, CheckCircle2, Users, ShieldAlert,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { formBandForForm } from '@/lib/financeCore';
import { resolveFeeStatus, type FeeStatus } from '@/lib/feeStatus';
import { currentTermKey, termLabel, type TermKey } from '@/lib/school.config';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TONE: Record<FeeStatus['tone'], string> = {
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  blue: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  slate: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

export default function FeeStatusOverview() {
  const { students, classes } = useData();
  const fin = useFinanceData();

  const [term, setTerm] = useState<TermKey>(currentTermKey());
  const [classId, setClassId] = useState('');

  const year = String(new Date().getFullYear());

  /* Every enrolled pupil, resolved once. School-wide before per-class,
     because a screen that made you pick a class first would only find an
     unbilled joiner if somebody looked at the right one. */
  const rows = useMemo(() => {
    if (fin.loading) return [];
    const byClass = new Map(classes.map((c) => [c.id, c]));

    return students
      .filter((s) => (s.status ?? 'active') === 'active')
      .map((s) => {
        const cls = byClass.get(s.currentClass ?? '');
        const bandKey = cls ? formBandForForm(cls.form) : '';
        const structure = fin.feeStructures.find(
          (f) => f.year === year && f.bandKey === bandKey,
        );
        const invoice = fin.invoices.find(
          (i) => i.studentId === s.id && i.term === term && i.year === year
            && i.status !== 'void',
        );
        return {
          student: s,
          className: cls?.name ?? '—',
          classId: cls?.id ?? '',
          status: resolveFeeStatus({ invoice, structure, term }),
        };
      })
      .sort((a, b) => (a.student.fullName ?? '').localeCompare(b.student.fullName ?? ''));
  }, [students, classes, fin, term, year]);

  const unaccounted = rows.filter((r) => r.status.needsAttention);
  const shown = classId ? rows.filter((r) => r.classId === classId) : rows;

  if (fin.loading) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const invoiced = rows.filter((r) => r.status.handling === 'invoiced');
  const owing = invoiced.filter((r) => (r.status.balance ?? 0) > 0.005);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={ReceiptText} tone="navy"
        title="Fee Status"
        subtitle="Whether every enrolled pupil is accounted for this term"
        actions={(
          <div className="flex gap-2">
            <select value={term} onChange={(e) => setTerm(e.target.value as TermKey)}
              className={glassFieldClass('max-w-[150px] text-sm')}>
              {(['Term1', 'Term2', 'Term3'] as TermKey[]).map((t) => (
                <option key={t} value={t}>{termLabel(t)}</option>
              ))}
            </select>
            <select value={classId} onChange={(e) => setClassId(e.target.value)}
              className={glassFieldClass('max-w-[170px] text-sm')}>
              <option value="">All classes</option>
              {[...classes].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      />

      {/* School-wide, and before anything else on the page. */}
      {unaccounted.length > 0 ? (
        <SoftCard className="mb-5 border-l-4 border-red-400">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
            <ShieldAlert size={15} className="inline mr-1 -mt-0.5" />
            {unaccounted.length} pupil{unaccounted.length === 1 ? ' is' : 's are'} enrolled and
            billable this term with no invoice and no other arrangement.
          </p>
          <p className="text-[12px] text-gray-600 dark:text-gray-300">
            Usually a joiner who arrived after the billing run. Left alone they stay unbilled
            for the whole term, and it surfaces when the year&rsquo;s income does not add up.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {unaccounted.slice(0, 12).map((r) => (
              <span key={r.student.id}
                className="text-[12px] px-2 py-0.5 rounded bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                {r.student.fullName} · {r.className}
              </span>
            ))}
            {unaccounted.length > 12 && (
              <span className="text-[12px] text-gray-500 px-1">
                and {unaccounted.length - 12} more
              </span>
            )}
          </div>
        </SoftCard>
      ) : (
        <SoftCard className="mb-5 border-l-4 border-emerald-400">
          <p className="text-sm text-emerald-800 dark:text-emerald-300">
            <CheckCircle2 size={15} className="inline mr-1 -mt-0.5" />
            Every enrolled pupil is accounted for this term.
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <StatChip tone="navy" icon={Users} value={String(rows.length)} label="Enrolled" />
        <StatChip tone="slate" value={String(invoiced.length)} label="Invoiced" />
        <StatChip tone={owing.length ? 'amber' : 'emerald'}
          value={money(owing.reduce((n, r) => n + (r.status.balance ?? 0), 0))}
          label={`Outstanding across ${owing.length}`} />
        <StatChip tone={unaccounted.length ? 'rose' : 'emerald'}
          icon={unaccounted.length ? AlertTriangle : CheckCircle2}
          value={String(unaccounted.length)} label="Unaccounted for" />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={ReceiptText} tone="navy"
          title={classId ? classes.find((c) => c.id === classId)?.name ?? 'Class' : 'Every pupil'}
          subtitle="The reason is on every row — “no invoice” is not one state" />
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white dark:bg-gray-800">
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Pupil</th>
                <th className="px-2 font-semibold">Class</th>
                <th className="px-2 font-semibold">How fees are handled</th>
                <th className="px-2 font-semibold text-right">Billed</th>
                <th className="px-2 pr-4 font-semibold text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.student.id}
                  className={`border-b border-gray-50 dark:border-gray-800 ${
                    r.status.needsAttention ? 'bg-red-50/50 dark:bg-red-950/20' : ''
                  }`}>
                  <td className="py-2 pl-4 pr-2 text-gray-900 dark:text-white">
                    {r.student.fullName}
                  </td>
                  <td className="px-2 text-gray-500">{r.className}</td>
                  <td className="px-2">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${TONE[r.status.tone]}`}>
                      {r.status.label}
                    </span>
                    {r.status.detail && (
                      <span className="block text-[11px] text-gray-400 mt-0.5">
                        {r.status.detail}
                      </span>
                    )}
                  </td>
                  <td className="px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {r.status.total != null ? money(r.status.total) : ''}
                  </td>
                  <td className={`px-2 pr-4 text-right tabular-nums ${
                    (r.status.balance ?? 0) > 0.005 ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-gray-400'
                  }`}>
                    {r.status.balance != null ? money(r.status.balance) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {shown.length === 0 && (
          <p className="p-6 text-sm text-gray-500 text-center">
            No enrolled pupils in that class.
          </p>
        )}
      </SoftCard>
    </div>
  );
}
